/**
 * Sorting for a list of stay properties.
 *
 * Lifted out of the city page when that page was retired in favour of
 * /stays/{type}: the rule about unpriced properties is the kind of detail that
 * gets re-derived wrongly if it is left to be rewritten from scratch.
 */
export type StaysCitySortOption = "recommended" | "price_asc" | "rating";

/**
 * "recommended" is the curated order the API already returns (no field to
 * sort by, so the array is left untouched). Properties without a live price
 * are pushed to the end of a price sort rather than sorted arbitrarily —
 * `null <=> null` would otherwise be a coin flip depending on Array.sort's
 * stability guarantees, and a priced property must never rank below an
 * unpriced one.
 */
export function sortStayProperties<
  T extends { price: { sellTotal: number } | null; starRating: number | null },
>(properties: T[], sort: StaysCitySortOption): T[] {
  if (sort === "price_asc") {
    return [...properties].sort((a, b) => {
      if (a.price === null && b.price === null) return 0;
      if (a.price === null) return 1;
      if (b.price === null) return -1;
      return a.price.sellTotal - b.price.sellTotal;
    });
  }
  if (sort === "rating") {
    return [...properties].sort(
      (a, b) => (b.starRating ?? 0) - (a.starRating ?? 0),
    );
  }
  return properties;
}
